import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts'
import {
  useGetApiCompaniesCurrentActionmodules,
  useGetApiCompaniesCurrentEmployeesReportingModulecompletionActionModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { FilterRowCharts } from '@/components/common/filters/FilterRowCharts'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { ChartCard } from '../components/ChartCard'
import type { DashboardDateFilter } from '../dateFilter'
import { completionRate, formatBucketAxisLabel, formatBucketRange } from './bucketLabels'

interface Props {
  filter: DashboardDateFilter
  isFilterValid: boolean
}

export function ModuleCompletionChart({ filter, isFilterValid }: Props) {
  const { t } = useTranslation('dashboard')
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null)

  const { data: modulesData } = useGetApiCompaniesCurrentActionmodules()
  const modules = modulesData?.data ?? []
  // Until the user picks one, the first module is shown
  const moduleId = selectedModuleId ?? modules[0]?.id ?? null

  const { data, isFetching, isError } = useGetApiCompaniesCurrentEmployeesReportingModulecompletionActionModuleId(
    moduleId ?? 0,
    filter,
    { query: { enabled: moduleId != null && isFilterValid } }
  )

  const buckets = data?.data?.buckets ?? []
  const chartData = buckets.map((bucket) => ({
    name: formatBucketAxisLabel(bucket.startDate, filter.bucketSize),
    range: formatBucketRange(bucket.startDate, bucket.endDate),
    total: bucket.total,
    completed: bucket.completed,
    rate: completionRate(bucket.completed, bucket.total),
  }))
  const isEmpty = !isFilterValid || moduleId == null || chartData.every((bucket) => bucket.total === 0)

  // Summed over all buckets; an instance spanning two buckets counts twice, like in the chart itself
  const sumTotal = buckets.reduce((sum, bucket) => sum + bucket.total, 0)
  const sumCompleted = buckets.reduce((sum, bucket) => sum + bucket.completed, 0)
  const overallRate = completionRate(sumCompleted, sumTotal)

  function controls() {
    // Without instances there is no rate to show
    if (isEmpty || overallRate == null) return undefined
    return (
      <FilterRowCharts>
        <span className="text-sm text-muted-foreground">
          {t('charts.moduleCompletion.summary', {
            rate: overallRate,
            completed: sumCompleted.toLocaleString('de-DE'),
            total: sumTotal.toLocaleString('de-DE'),
          })}
        </span>
      </FilterRowCharts>
    )
  }

  function chart() {
    return (
      <LoaderGeneral isLoading={isFetching} isError={isError} isEmpty={isEmpty} className="h-[300px]">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <defs>
              <linearGradient id="moduleCompletionFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--muted-foreground)" strokeOpacity={0.25} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--foreground)' }} tickLine={false} axisLine={false} minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ stroke: 'var(--muted-foreground)', strokeOpacity: 0.4 }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-sm">
                    <p className="font-semibold text-card-foreground mb-0.5">{payload[0].payload.range}</p>
                    <p className="text-primary">
                      {t('charts.moduleCompletion.tooltipCompleted', { completed: payload[0].payload.completed, total: payload[0].payload.total })}
                      {payload[0].payload.rate != null && <span className="font-bold"> ({payload[0].payload.rate} %)</span>}
                    </p>
                    <p className="text-muted-foreground">
                      {t('charts.moduleCompletion.tooltipOpen', { count: payload[0].payload.total - payload[0].payload.completed })}
                    </p>
                  </div>
                ) : null
              }
            />
            <Legend formatter={(value) => t(`charts.moduleCompletion.legend.${value}`)} />
            <Area
              type="monotone"
              dataKey="completed"
              stroke="var(--chart-1)"
              strokeWidth={2}
              fill="url(#moduleCompletionFill)"
              dot={{ r: 3, fill: 'var(--chart-1)' }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="var(--muted-foreground)"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={{ r: 3, fill: 'var(--muted-foreground)' }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </LoaderGeneral>
    )
  }

  function moduleSelect() {
    return (
      <Select
        value={moduleId != null ? String(moduleId) : undefined}
        onValueChange={(value) => setSelectedModuleId(Number(value))}
      >
        <SelectTrigger className="text-sm w-64" aria-label={t('charts.moduleCompletion.module')}>
          <SelectValue placeholder={t('charts.moduleCompletion.noModule')} />
        </SelectTrigger>
        <SelectContent>
          {modules.map((module) => (
            <SelectItem key={module.id} value={String(module.id)}>{module.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <ChartCard
      title={t('charts.moduleCompletion.title')}
      headerAction={moduleSelect()}
      chart={chart()}
      controls={controls()}
    />
  )
}
